<template>
  <GMapMap
    ref="map"
    :center="internalVisibilityMapState.center"
    :max-zoom="20"
    :zoom="mapZoom"
    :options="{
      ...defaultOptions,
      disableDoubleClickZoom: true // Disable zooming when double-clicking
    }"
    class="h-fit"
    map-type-id="roadmap"
    @zoom_changed="updateArrowScale"
  >
    <template
      v-for="point in points"
      :key="`${point.id}_parent_template`"
    >
      <!-- New Circle for Current Position -->
      <GMapCircle
        v-if="internalVisibilityMapRef.currentPosition && internalVisibilityMapRef.showCurrentPosition"
        :radius="20"
        :center="{
          lat: internalVisibilityMapRef.currentPosition.lat,
          lng: internalVisibilityMapRef.currentPosition.lng,
        }"
        :options="{
          clickable: false,
          strokeWeight: 2,
          strokeColor: '#000000',
          fillColor: '#00FF00',
          fillOpacity: 0.7,
        }"
      />
      <GMapInfoWindow
        v-if="showInfo && point.gisLocNew"
        :key="`${point.id}_infoWindow`"
        :opened="shownPoints[point.id]?.new ?? false"
        :options="{
          position: {
            lat: point.gisLocNew.lat,
            lng: point.gisLocNew.long,
          },
        }"
        :closeclick="true"
        @closeclick="setShownPoint(point.id, 'new', false)"
      >
        <div class="info">
          <slot
            name="info"
            :point="point"
            data-type="new"
          />
        </div>
      </GMapInfoWindow>
      <GMapInfoWindow
        v-if="showInfo && point.gisLocOld"
        :key="`${point.id}.${point.gisLocOld.lat}.${point.gisLocOld.long}.old`"
        :opened="shownPoints[point.id]?.old ?? false"
        :options="{
          position: {
            lat: point.gisLocOld.lat,
            lng: point.gisLocOld.long,
          },
        }"
        :closeclick="true"
        @closeclick="setShownPoint(point.id, 'old', false)"
      >
        <div class="info">
          <slot
            name="info"
            :point="point"
            data-type="old"
          />
        </div>
      </GMapInfoWindow>
      <GMapPolyline
        v-if="point.gisLocNew && point.gisLocOld"
        :key="`${point.id}_line`"
        :path="[
          { lat: point.gisLocOld.lat, lng: point.gisLocOld.long },
          { lat: point.gisLocNew.lat, lng: point.gisLocNew.long },
        ]"
        :options="{
          strokeColor: '#0088FF',
          strokeOpacity: 1,
          strokeWeight: 3,
          clickable: true,
          icons: [
            {
              icon: {
                path: 2,
                strokeOpacity: 1,
                scale: 2,
              },
              offset: '0',
              repeat: '30px',
            },
          ]
        }"
        @click="() => {
          const isNewOpen = shownPoints[point.id]?.new ?? false;
          const isOldOpen = shownPoints[point.id]?.old ?? false;

          // If both are closed, open both
          setShownPoint(point.id, 'new', !isNewOpen && !isOldOpen);
        }"
      />
      <GMapCircle
        v-if="point.gisLocOld"
        :radius="point.radius"
        :center="{
          lat: point.gisLocOld?.lat,
          lng: point.gisLocOld?.long,
        }"
        :options="{
          clickable: true,
          strokeWeight: 0,
          fillColor: '#2831ff',
          fillOpacity: 0.8,
        }"
        @click="setShownPoint(point.id, 'old',true)"
      />
      <!-- render last to prevent wrong handler on overlap -->
      <GMapCircle
        v-if="point.gisLocNew"
        :radius="point.radius"
        :center="{
          lat: point.gisLocNew?.lat,
          lng: point.gisLocNew?.long,
        }"
        :options="{
          clickable: true,
          strokeWeight: 0,
          fillColor: '#fc000f',
          fillOpacity: 0.8,
        }"
        @click="setShownPoint(point.id, 'new', true)"
      />
    </template>

  </GMapMap>
</template>

<script lang="ts" setup>
import {
  computed, onMounted, ref, watch,
} from 'vue';
import { defaultCenter, defaultOptions } from '@/utils/map';
import type { VisibilityMapState, VisibilityTransferMapData } from '@/entities/Entities';

interface Props {
  points?: VisibilityTransferMapData[];
  showInfo?: boolean;
  hideInnerCircle?: boolean;
  computeCenter?: number;
  visibilityMapState: VisibilityMapState,
  visibilityMapRef: null,
}

const props = defineProps<Props>();
const map = ref(null);
const points = computed(() => props.points?.filter((point) => point.gisLocNew));

const shownPoints = ref<Record<string, { new: boolean; old: boolean }>>({});
const mapZoom = ref(8); // Initial zoom level

const emit = defineEmits(['update:locationStack', 'update:visibilityMapState', 'update:visibilityMapRef', 'click']);

const internalVisibilityMapState = computed({
  get: () => props.visibilityMapState,
  set: (newValue) => emit('update:visibilityMapState', newValue),
});
const internalVisibilityMapRef = computed({
  get: () => props.visibilityMapState,
  set: (newValue) => emit('update:visibilityMapRef', newValue),
});

const setShownPoint = (pointId: string, type: 'new' | 'old', shown: boolean) => {
  if (!shownPoints.value[pointId]) {
    shownPoints.value[pointId] = {
      new: false,
      old: false,
    };
  }
  shownPoints.value[pointId][type] = shown;
};

const LAT_PER_METER = 1.0 / 111000;
const LONG_PER_METER = 1.0 / 76000;

const computeCenter = () => {
  if (points.value?.length) {
    // eslint-disable-next-line no-undef
    const bounds = new google.maps.LatLngBounds();
    for (const point of points.value) {
      if (point.gisLocNew) {
        bounds.extend({
          lat: point.gisLocNew.lat + LAT_PER_METER * point.radius,
          lng: point.gisLocNew.long + LONG_PER_METER * point.radius,
        });
        bounds.extend({
          lat: point.gisLocNew.lat - LAT_PER_METER * point.radius,
          lng: point.gisLocNew.long - LONG_PER_METER * point.radius,
        });
        bounds.extend({
          lat: point.gisLocNew.lat - LAT_PER_METER * point.radius,
          lng: point.gisLocNew.long + LONG_PER_METER * point.radius,
        });
        bounds.extend({
          lat: point.gisLocNew.lat + LAT_PER_METER * point.radius,
          lng: point.gisLocNew.long - LONG_PER_METER * point.radius,
        });
      }

      if (point.gisLocOld) {
        bounds.extend({
          lat: point.gisLocOld.lat + LAT_PER_METER * point.radius,
          lng: point.gisLocOld.long + LONG_PER_METER * point.radius,
        });
        bounds.extend({
          lat: point.gisLocOld.lat - LAT_PER_METER * point.radius,
          lng: point.gisLocOld.long - LONG_PER_METER * point.radius,
        });
        bounds.extend({
          lat: point.gisLocOld.lat - LAT_PER_METER * point.radius,
          lng: point.gisLocOld.long + LONG_PER_METER * point.radius,
        });
        bounds.extend({
          lat: point.gisLocOld.lat + LAT_PER_METER * point.radius,
          lng: point.gisLocOld.long - LONG_PER_METER * point.radius,
        });
      }
    }
    const boundsCenter = bounds.getCenter();
    internalVisibilityMapState.value.center.value = {
      lat: boundsCenter.lat(),
      lng: boundsCenter.lng(),
    };
    map.value.fitBounds(bounds);
  } else {
    internalVisibilityMapState.value.center.value = defaultCenter;
  }
};

const updateArrowScale = () => {
  if (map.value) {
    mapZoom.value = map.value.$mapObject.zoom;
  }
};

watch(
  () => [props.points, props.computeCenter, internalVisibilityMapState.value.center],
  () => {
    computeCenter();
  },
);

onMounted(async () => {
  await map.value.$mapPromise;
  internalVisibilityMapRef.value = map.value;
  computeCenter();
});

</script>
<style lang="scss" scoped>
:deep(.info) {
  h6 {
    font-size: 16px;
    padding-bottom: 0.5rem;
    font-weight: 700;
  }
}
</style>
